import { useState, useCallback } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { Loader2, GitBranch } from 'lucide-react'
import { usePlanMetadata } from '@/hooks/usePlanMetadata'
import { PlanDetailReportTab } from '@/components/PlanDetail/PlanDetailReportTab'
import { PLAN_DETAIL_TABS } from '@/config/powerbiReports'
import {
  useApproveSubmission,
  useSendBackSubmission,
  useMarkSubmissionsSeen,
  useUnapproveSubmission,
} from '@/hooks/useManagerActions'
import { CommentsTab } from '@/components/PlanDetail/CommentsTab'
import { ApprovalDetailHeader } from '@/components/Approvals/ApprovalDetailHeader'
import {
  ApprovalDetailTabs,
  type ApprovalTab,
} from '@/components/Approvals/ApprovalDetailTabs'
import { ApprovalDetailFooter } from '@/components/Approvals/ApprovalDetailFooter'
import { ReadOnlyProductsTable } from '@/components/Approvals/ReadOnlyProductsTable'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Textarea } from '@/components/ui/textarea'
import { buildHierarchyBreadcrumb } from '@/utils/formatters'
import type { ManagerSubmissionSummary } from '@/api/managerSubmissions'
import { useEffect } from 'react'

export default function ApprovalDetail() {
  const { submissionId } = useParams<{ submissionId: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  const submission = (
    location.state as { submission?: ManagerSubmissionSummary }
  )?.submission

  const [activeTab, setActiveTab] = useState<ApprovalTab>('details')
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [sendBackDialogOpen, setSendBackDialogOpen] = useState(false)
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false)
  const [unapproveNotes, setUnapproveNotes] = useState('')

  const planId = submission?.plan_id
  const versionId = submission?.version_id

  // Fetch plan metadata for name + hierarchy
  const { data: planData, isLoading: planLoading } = usePlanMetadata(
    planId ?? ''
  )

  // Mark seen
  const markSeen = useMarkSubmissionsSeen()
  useEffect(() => {
    if (submissionId && submission && !submission.seen_by_approver) {
      markSeen.mutate([submissionId])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId])

  // Mutations
  const approveMutation = useApproveSubmission()
  const sendBackMutation = useSendBackSubmission()
  const unapproveMutation = useUnapproveSubmission()

  const isPending = submission?.submission_status === 'PENDING'
  const isApproved = submission?.submission_status === 'APPROVED'

  const handleConfirmApprove = useCallback(() => {
    if (submissionId) {
      approveMutation.mutate(submissionId, {
        onSuccess: () => {
          setApproveDialogOpen(false)
          navigate('/approvals')
        },
        onSettled: () => {
          setApproveDialogOpen(false)
        },
      })
    }
  }, [submissionId, approveMutation, navigate])

  const handleConfirmSendBack = useCallback(() => {
    if (submissionId) {
      sendBackMutation.mutate(submissionId, {
        onSuccess: () => {
          setSendBackDialogOpen(false)
          navigate('/approvals')
        },
        onSettled: () => {
          setSendBackDialogOpen(false)
        },
      })
    }
  }, [submissionId, sendBackMutation, navigate])

  const handleConfirmUnapprove = useCallback(() => {
    if (submissionId) {
      unapproveMutation.mutate(
        {
          submissionId,
          body: unapproveNotes.trim()
            ? { decision_notes: unapproveNotes.trim() }
            : undefined,
        },
        {
          onSuccess: () => {
            setUnapproveDialogOpen(false)
            setUnapproveNotes('')
            navigate('/approvals')
          },
          onSettled: () => {
            setUnapproveDialogOpen(false)
          },
        }
      )
    }
  }, [submissionId, unapproveMutation, unapproveNotes, navigate])

  if (!submission) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">
          Submission data not found. Please go back to{' '}
          <button
            onClick={() => navigate('/approvals')}
            className="text-primary underline cursor-pointer"
          >
            Approvals
          </button>
          .
        </p>
      </div>
    )
  }

  const primaryAttributes =
    planData?.primary_attributes ?? planData?.primary_attribute_ids ?? []
  const planName = planData?.name ?? submission.plan_name
  const hierarchy = planData?.hierarchy
    ? buildHierarchyBreadcrumb(planData.hierarchy)
    : null

  return (
    <>
      {/* Approve Dialog */}
      <ConfirmationDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        title="Approve submission"
        description={`Are you sure you want to approve "${planName}"?`}
        confirmText="Approve"
        cancelText="Cancel"
        onConfirm={handleConfirmApprove}
        isLoading={approveMutation.isPending}
      />

      {/* Send Back Dialog */}
      <ConfirmationDialog
        open={sendBackDialogOpen}
        onOpenChange={setSendBackDialogOpen}
        title="Send back submission"
        description={`Are you sure you want to send back "${planName}" to the planner?`}
        confirmText="Send Back"
        cancelText="Cancel"
        onConfirm={handleConfirmSendBack}
        variant="destructive"
        isLoading={sendBackMutation.isPending}
      />

      {/* Unapprove Dialog */}
      <ConfirmationDialog
        open={unapproveDialogOpen}
        onOpenChange={open => {
          setUnapproveDialogOpen(open)
          if (!open) setUnapproveNotes('')
        }}
        title="Unapprove submission"
        description={`Are you sure you want to unapprove "${planName}"? The plan will return to Draft status.`}
        confirmText="Unapprove"
        cancelText="Cancel"
        onConfirm={handleConfirmUnapprove}
        variant="destructive"
        isLoading={unapproveMutation.isPending}
      >
        <div className="space-y-2 py-2">
          <label className="text-sm font-medium">Notes (optional)</label>
          <Textarea
            placeholder="Add a reason for unapproving..."
            value={unapproveNotes}
            onChange={e => setUnapproveNotes(e.target.value)}
            className="min-h-[80px] resize-none"
            maxLength={2000}
          />
        </div>
      </ConfirmationDialog>

      <div className="flex flex-col h-full">
        {/* Header */}
        <ApprovalDetailHeader />

        {/* Plan info + tabs */}
        <div className="bg-white px-8 py-6 border-b border-border">
          {planLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-foreground">
                {planName}
              </h2>
              {hierarchy && (
                <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                  <GitBranch className="h-3.5 w-3.5" />
                  <span>{hierarchy}</span>
                </div>
              )}
            </>
          )}
          <div className="mt-4">
            <ApprovalDetailTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 flex gap-5 bg-[#f8f8f8] p-6 overflow-hidden">
          {/* Main content */}
          <div className="flex-1 overflow-auto">
            {activeTab === 'financial' ? (
              planId ? (
                <PlanDetailReportTab
                  reportId={PLAN_DETAIL_TABS.summary.reportId}
                  pageId={PLAN_DETAIL_TABS.summary.pageId}
                  label="Financial Summary"
                  filterTarget={PLAN_DETAIL_TABS.summary.filterTarget}
                  filterValue={planId}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No plan data available
                </div>
              )
            ) : planId && versionId ? (
              <ReadOnlyProductsTable
                planId={planId}
                versionId={versionId}
                primaryAttributes={primaryAttributes}
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                No version data available
              </div>
            )}
          </div>

          {/* Comments sidebar */}
          <div className="w-[300px] shrink-0 overflow-auto">
            {planId && <CommentsTab planId={planId} />}
          </div>
        </div>

        {/* Footer */}
        <ApprovalDetailFooter
          isPending={isPending}
          isApproved={isApproved}
          isApproving={approveMutation.isPending}
          isSendingBack={sendBackMutation.isPending}
          isUnapproving={unapproveMutation.isPending}
          onApprove={() => setApproveDialogOpen(true)}
          onSendBack={() => setSendBackDialogOpen(true)}
          onUnapprove={() => setUnapproveDialogOpen(true)}
        />
      </div>
    </>
  )
}
